import React from 'react';

/**
 * RedactorProgress – progress bar + per-file status during redaction.
 */
function RedactorProgress({ jobStatus }) {
  if (!jobStatus) return null;

  const { status, progress = 0, files, stage, completed_pages: completedPages = 0, total_pages: totalPages = 0 } = jobStatus;
  const isProcessing = status === 'processing';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';

  const barClass = isFailed
    ? 'bg-danger'
    : isCompleted
    ? 'bg-success'
    : 'bg-primary progress-bar-striped progress-bar-animated';

  const stageLabel = {
    preparing_document: 'Preparing document',
    processing_pages: 'Redacting pages',
    assembling_document: 'Assembling final document',
    completed: 'Complete',
  }[stage] || (isProcessing ? 'Starting redaction' : 'Processing');

  const progressDetail = stage === 'processing_pages' && totalPages > 0
    ? `${completedPages} of ${totalPages} pages complete`
    : stageLabel;

  return (
    <div className="mb-4">
      <h5 className="mb-3">
        <i className="bi bi-activity me-2"></i>
        {isProcessing ? 'Redacting...' : isCompleted ? 'Redaction Complete' : isFailed ? 'Redaction Failed' : 'Processing'}
      </h5>

      {isProcessing && (
        <div className="d-flex justify-content-between align-items-center mb-2 small">
          <span className="fw-semibold">{stageLabel}</span>
          <span className="text-muted">{progressDetail}</span>
        </div>
      )}

      <div className="progress mb-3" style={{ height: 24 }}>
        <div
          className={`progress-bar ${barClass}`}
          role="progressbar"
          style={{ width: `${Math.max(0, Math.min(100, progress))}%`, minWidth: progress > 0 ? 44 : 0 }}
          aria-valuenow={Math.round(progress)}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          {Math.round(progress)}%
        </div>
      </div>

      {files && files.length > 0 && (
        <div className="list-group list-group-flush">
          {files.map((f) => (
            <div
              key={f.file_id}
              className="list-group-item d-flex justify-content-between align-items-center py-2"
            >
              <div className="d-flex align-items-center text-truncate" style={{ maxWidth: '60%' }}>
                {f.status === 'completed' && (
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                )}
                {f.status === 'processing' && (
                  <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                    <span className="visually-hidden">Processing...</span>
                  </div>
                )}
                {f.status === 'failed' && (
                  <i className="bi bi-x-circle-fill text-danger me-2"></i>
                )}
                {f.status === 'pending' && (
                  <i className="bi bi-clock text-muted me-2"></i>
                )}
                <span className="text-truncate">{f.original_filename}</span>
              </div>
              <div>
                {f.status === 'completed' && (
                  <span className="badge bg-success">
                    {Object.values(f.match_summary || {}).reduce((a, b) => a + b, 0)} match(es)
                  </span>
                )}
                {f.status === 'failed' && (
                  <span className="badge bg-danger">Error</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isCompleted && (
        <div className="alert alert-success mt-3 mb-0 py-2">
          <i className="bi bi-check2-all me-2"></i>
          All files processed. {jobStatus.total_matches} total redaction(s) found.
        </div>
      )}

      {isFailed && jobStatus.error && (
        <div className="alert alert-danger mt-3 mb-0 py-2">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {jobStatus.error}
        </div>
      )}
    </div>
  );
}

export default RedactorProgress;
